import SecretInput from "@/pages/admin/components/SecretInput";

export interface PaymentKeys {
  mode: "test" | "live";
  stripe_test_secret_key: string;
  stripe_live_secret_key: string;
  paypal_test_client_id: string;
  paypal_test_secret: string;
  paypal_live_client_id: string;
  paypal_live_secret: string;
}

interface PaymentKeysSectionProps {
  value: PaymentKeys;
  onChange: (patch: Partial<PaymentKeys>) => void;
}

const labelClass = "block text-xs font-medium text-foreground-600 mb-1.5";

export default function PaymentKeysSection({
  value,
  onChange,
}: PaymentKeysSectionProps) {
  return (
    <section className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-8">
      <h2 className="font-heading text-base font-semibold text-foreground-950">
        Clés API & environnement
      </h2>
      <p className="mt-1 text-sm text-foreground-600">
        Saisissez vos clés Stripe et PayPal, puis choisissez l'environnement
        actif (test ou production).
      </p>

      <div className="mt-5">
        <p className="text-sm font-medium text-foreground-700 mb-2">
          Environnement actif
        </p>
        <div className="inline-flex rounded-full border border-background-300 bg-background-100 p-1">
          {(["test", "live"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => onChange({ mode: m })}
              className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-5 py-2 text-sm font-semibold transition-all duration-200 cursor-pointer ${
                value.mode === m
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              {m === "test" ? (
                <>
                  <i className="ri-flask-line"></i>
                  Mode test
                </>
              ) : (
                <>
                  <i className="ri-rocket-line"></i>
                  Production
                </>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
            <i className="ri-bank-card-line"></i>
          </span>
          <h3 className="text-sm font-semibold text-foreground-800">Stripe</h3>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Clé secrète (test)</label>
            <SecretInput
              value={value.stripe_test_secret_key}
              onChange={(v) => onChange({ stripe_test_secret_key: v })}
              placeholder="sk_test_…"
            />
          </div>
          <div>
            <label className={labelClass}>Clé secrète (production)</label>
            <SecretInput
              value={value.stripe_live_secret_key}
              onChange={(v) => onChange({ stripe_live_secret_key: v })}
              placeholder="sk_live_…"
            />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
            <i className="ri-paypal-line"></i>
          </span>
          <h3 className="text-sm font-semibold text-foreground-800">PayPal</h3>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>ID client (test)</label>
            <input
              type="text"
              value={value.paypal_test_client_id}
              onChange={(e) =>
                onChange({ paypal_test_client_id: e.target.value })
              }
              placeholder="Client ID sandbox"
              autoComplete="off"
              spellCheck={false}
              className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
            />
          </div>
          <div>
            <label className={labelClass}>Clé secrète (test)</label>
            <SecretInput
              value={value.paypal_test_secret}
              onChange={(v) => onChange({ paypal_test_secret: v })}
              placeholder="Secret sandbox"
            />
          </div>
          <div>
            <label className={labelClass}>ID client (production)</label>
            <input
              type="text"
              value={value.paypal_live_client_id}
              onChange={(e) =>
                onChange({ paypal_live_client_id: e.target.value })
              }
              placeholder="Client ID live"
              autoComplete="off"
              spellCheck={false}
              className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition"
            />
          </div>
          <div>
            <label className={labelClass}>Clé secrète (production)</label>
            <SecretInput
              value={value.paypal_live_secret}
              onChange={(v) => onChange({ paypal_live_secret: v })}
              placeholder="Secret live"
            />
          </div>
        </div>
      </div>

      <p className="mt-5 text-xs text-foreground-500">
        Vos clés sont stockées de façon sécurisée et ne sont jamais exposées
        aux visiteurs du site.
      </p>
    </section>
  );
}